import {
  ComponentProps,
  lazy,
  PropsWithChildren,
  Suspense,
  useState,
} from 'react';

import { LoadingIndicator } from '@bambi/component-library/src/components/LoadingIndicator/LoadingIndicator';

import { useAppSelector } from 'src/app/store/hooks';
import { selectCurrentOrganizationHeadquartersLocation } from 'src/features/auth/selectors';

// Both the Map route and trip summary are rarely the user's first destination.
// Split them out since they're hefty
const MapView = lazy(() =>
  import('@aws-amplify/ui-react-geo').then((module) => ({
    default: module.MapView,
  }))
);

const NavigationControl = lazy(() =>
  import('react-map-gl').then((module) => ({
    default: module.NavigationControl,
  }))
);

export const BAMBI_MAPVIEW_DEFAULT_ZOOM_DURATION = 300;

export type MapViewProps = ComponentProps<typeof MapView>;

type BambiMapViewProps = PropsWithChildren & {
  mapViewProps?: MapViewProps;
};

export const DEFAULT_ZOOM_LEVEL = 9;

/**
 * Be sure to pass initial properties, like zoom and lat/long, inside
 * the mapViewProps.initialViewState object and not at the top level.
 */
export function BambiMapView({
  children,
  mapViewProps = {},
}: BambiMapViewProps) {
  const hqLocation = useAppSelector(
    selectCurrentOrganizationHeadquartersLocation
  );

  const [currentZoomLevelForTests, setCurrentZoomLevelForTests] =
    useState(DEFAULT_ZOOM_LEVEL);
  return (
    <>
      <div
        data-initial-address={hqLocation?.address}
        data-testid="current-zoom-level-for-tests"
        style={{ display: 'none' }}
      >
        {currentZoomLevelForTests}
      </div>
      <SuspendedMapView
        mapViewProps={{
          ...mapViewProps,
          initialViewState: {
            zoom: DEFAULT_ZOOM_LEVEL,
            latitude: hqLocation?.latitude,
            longitude: hqLocation?.longitude,
            ...mapViewProps.initialViewState, // Allow parent to override
            fitBoundsOptions: {
              duration: BAMBI_MAPVIEW_DEFAULT_ZOOM_DURATION,
              ...mapViewProps.initialViewState?.fitBoundsOptions, // Allow parent to override
            },
          },
          onZoom: (e) => setCurrentZoomLevelForTests(e.viewState.zoom),
        }}
      >
        {children}
      </SuspendedMapView>
    </>
  );
}

interface SuspendedMapViewProps extends PropsWithChildren {
  mapViewProps: MapViewProps;
  includeNavigationControl?: boolean;
}

export function SuspendedMapView({
  mapViewProps,
  includeNavigationControl = true,
  children,
}: SuspendedMapViewProps) {
  return (
    <Suspense fallback={<LoadingIndicator />}>
      <style>{
        // TODO: Tried finding a MapView prop and also stitches
        // There is inline styling in the MapView component being set
        // One thing I didn't try is adding a container that's passed to stitches
        // and then using https://stitches.dev/docs/styling#descendant-selector
        // This is simple and works though
        `
        .maplibregl-map.mapboxgl-map {
          height: 100% !important;
          width: 100% !important;
        }
        .mapboxgl-popup-close-button {
          display: none;
        }
    `
      }</style>
      <MapView {...mapViewProps}>
        {includeNavigationControl ? (
          <NavigationControl
            position="bottom-right"
            showZoom
            showCompass={false}
          />
        ) : null}
        {children}
      </MapView>
    </Suspense>
  );
}
